import React, { ReactElement, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { Dispatch } from 'redux';
import { actions as locationTaskActions } from '@Actions/locationTaskActions';
import * as types from '@Constants/actionTypes';
import { CUSTOMER_ROLES } from '@Constants/roles';
import Notification from '@Common/Notification';
import MentionableTextArea from '@Common/MentionableTextArea/MentionableTextArea';
import LoadingIndicator from '@Common/LoadingIndicator/LoadingIndicator';
import CommentRow from './CommentRow';
import { CommentDetailsProps, MentionableUser } from './Types';
import {
    useLoadComments,
    useListMentionableUsers,
    useCreateComment,
    useUpdateComment,
    useDeleteComment,
} from '@Apis/locationTasks';
import './Comments.scss';

interface CommentsProps {
    locationTaskId: number;
    locationKey: string;
}

export interface CommentsHandle {
    hasDraft: () => boolean;
    clearDraft: () => void;
    postDraft: () => Promise<boolean>;
}

const Comments = forwardRef<CommentsHandle, CommentsProps>((props: CommentsProps, ref): ReactElement => {

    const pageSize = 3;

    const { locationTaskId, locationKey } = props;

    const [comment, setComment] = useState<string>('');
    const [comments, setComments] = useState<CommentDetailsProps[]>([]);
    const [commentCount, setCommentCount] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(1);

    const customerRole = useSelector((state: { user: { customerRole: string } }) => state.user.customerRole);
    const isOwner = customerRole === CUSTOMER_ROLES.Owner.name;

    const { data: commentsData, isLoading, isError: isCommentsError } = useLoadComments(locationTaskId, {
        enabled: locationTaskId > 0,
        pageNumber,
        pageSize,
    });

    const { data: mentionableUsersData, isError: isMentionableUsersError } = useListMentionableUsers(locationKey, {
        enabled: !!locationKey,
    });

    const createCommentMutation = useCreateComment(locationTaskId, (result) => {
        if (result) {
            Notification('success', 'Success', 'Comment created successfully');
            setComment('');
            setComments([]);
            setPageNumber(1);
        }
    });

    const updateCommentMutation = useUpdateComment(locationTaskId, (result) => {
        if (result) {
            Notification('success', 'Success', 'Comment updated successfully');
            setComments([]);
            setPageNumber(1);
        }
    });

    const deleteCommentMutation = useDeleteComment(locationTaskId, (result) => {
        if (result) {
            Notification('success', 'Success', 'Comment deleted successfully');
            setComments([]);
            setPageNumber(1);
        }
    });

    const isPosting = createCommentMutation.isPending || updateCommentMutation.isPending || deleteCommentMutation.isPending;
    const mentionableUsers = (mentionableUsersData?.users || []) as MentionableUser[];

    const onCommentCreate = (value: string) => {
        if (isPosting || !value.trim()) return false;
        createCommentMutation.mutate(
            value,
            {
                onError: (error) => {
                    Notification('error', 'Error', (error as Error).message || 'Failed to create comment');
                    return false
                },
            }
        );
        return true
    };

    const onCommentDelete = async (commentId: number) => {
        deleteCommentMutation.mutate(
            commentId,
            {
                onError: (error) => {
                    Notification('error', 'Error', (error as Error).message || 'Failed to delete comment');
                },
            }
        );
    };

    const onCommentUpdate = async (commentId: number, value: string) => {
        updateCommentMutation.mutate(
            { commentId, comment: value },
            {
                onError: (error) => {
                    Notification('error', 'Error', (error as Error).message || 'Failed to update comment');
                },
            }
        );
    };

    const showMoreComments = () => {
        setPageNumber((prev) => prev + 1);
    };

    useEffect(() => {
        setComments([]);
        setCommentCount(0);
        setPageNumber(1);
    }, [locationTaskId]);

    useEffect(() => {
        if (commentsData?.comments) {
            pageNumber === 1
                ? setComments(commentsData.comments as CommentDetailsProps[])
                : setComments((prev) => [...prev, ...((commentsData.comments as CommentDetailsProps[]) || [])]);
            setCommentCount(commentsData.totalCount || 0);
        }
    }, [commentsData, pageNumber]);

    useEffect(() => {
        if (isCommentsError) {
            Notification('error', 'Error', 'Comments could not be loaded');
        }
    }, [isCommentsError]);

    useEffect(() => {
        if (isMentionableUsersError) {
            Notification('error', 'Error', 'Mentionable users could not be loaded');
        }
    }, [isMentionableUsersError]);
    useImperativeHandle(ref, () => ({
        hasDraft: () => !!comment.trim(),
        clearDraft: () => setComment(''),
        // The caller closes or saves the drawer on success, so the post has to settle first
        postDraft: async () => {
            if (isPosting || !comment.trim()) return false;
            try {
                await createCommentMutation.mutateAsync(comment);
                return true;
            } catch (error) {
                Notification('error', 'Error', (error as Error).message || 'Failed to create comment');
                return false;
            }
        },
    }), [comment, isPosting, createCommentMutation]);

    return (
        <LoadingIndicator isLoading={isLoading}>
            <div className='location-task-comments'>
                <div className='row'>
                    <div className='col-12'>
                        <div className='new-comment-container w-100'>
                            <MentionableTextArea
                                name='comment'
                                data={mentionableUsers}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                maxLength={2000}
                                placeHolder='What would you like to say? Type @ to tag a user in your comment.'
                            />
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <button
                                    type='button'
                                    title='Post'
                                    className='pacer-table-button post-button'
                                    onClick={() => onCommentCreate(comment)}
                                    disabled={isPosting || !comment.trim()}
                                >
                                    {isPosting ? 'Posting…' : 'Post'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {comments?.map((comment: CommentDetailsProps) => {
                    return <CommentRow key={comment.id} comment={comment} mentionableUsers={mentionableUsers} isOwner={isOwner} onCommentPost={onCommentUpdate} onCommentDelete={onCommentDelete} />;
                })}

                <div className='row'>
                    <div className='col-12 text-center'>
                        {
                            comments.length < commentCount &&
                            <div className="link-with-icon" onClick={() => showMoreComments()}>
                                <i className={'fa-thin fa-chevron-down'}></i>
                                <button type='button' title="Show More" className="btn btn-link">
                                    Show More
                                </button>
                            </div>
                        }
                    </div>
                </div>
            </div>
        </LoadingIndicator>
    );
});

export default Comments;
